import AdminHeader from "pages/Settings/AdminHeader";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import React, { useRef, useState } from "react";
import NotificationAlert from "react-notification-alert";
import Select from "react-select";
import {
  REGEX,
  SELECT_CUSTOM_STYLES,
  MAX_FILE_SIZE,
} from "variables/constants";
import { ROLES } from "variables/constants";
import { getUserDetails } from "actions/users";
import { updateUser, changePassword } from "actions/users";
import { userChangePassword } from "actions/users";

const initialFields = {
  fullName: {
    value: "",
    error: "",
  },
  email: {
    value: "",
    error: "",
  },
  mobileNo: {
    value: "",
    error: "",
  },
  dateOfBirth: {
    value: "",
    error: "",
  },
  gender: {
    value: "",
    error: "",
  },
  address: {
    value: "",
    error: "",
  },
  profilePhoto: {
    value: null,
    preview: "",
    error: "",
  },
  role: {
    value: "",
  },
  accountStatus: {
    value: "",
  },
  lastLogin: {
    value: "",
  },
  createdOn: {
    value: "",
  },
};

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
  { value: "Prefer not to say", label: "Prefer not to say" },
];

// Initial state for change password modal
const initialPasswordState = {
  oldPassword: {
    value: "",
    error: "",
    showPassword: false,
  },
  newPassword: {
    value: "",
    error: "",
    showPassword: false,
  },
  confirmPassword: {
    value: "",
    error: "",
    showPassword: false,
  },
};

function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(initialFields);
  const [userData, setUserData] = useState({});
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState(initialPasswordState);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const notificationAlertRef = useRef(null);
  const role = JSON.parse(localStorage.getItem("p"))?.role;

  React.useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    const res = await getUserDetails(0);
    if (res.code === 200) {
      setFormData({
        ...formData,
        fullName: { value: res.data.name, error: "" },
        email: { value: res.data.email, error: "" },
        mobileNo: { value: res.data.mobileNo, error: "" },
        address: { value: res.data.address || "", error: "" },
        companyName: { value: res.data.companyName || "", error: "" },
        role: { value: res.data.role },
        accountStatus: { value: res.data.status },
        createdOn: { value: res.data.createdAt },
      });
    }
  };

  const prepareUpdatePayload = () => {
    const isPublisher = formData.role.value === "Publisher";

    const payload = {
      email: formData.email.value,
      mobileNo: formData.mobileNo.value,
    };

    if (isPublisher) {
      payload.companyName = formData.companyName.value;
    } else {
      payload.name = formData.fullName.value;
    }

    if (isPublisher && formData.address.value) {
      payload.address = formData.address.value;
    }

    return payload;
  };

  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const handleSave = async () => {
    let hasErrors = false;
    const updatedFormData = { ...formData };

    const isPublisher = formData.role.value === "Publisher";

    if (isPublisher) {
      const companyNameError = getValidationError(
        "companyName",
        formData.companyName.value,
      );
      if (companyNameError) {
        updatedFormData.companyName.error = companyNameError;
        hasErrors = true;
      }
    } else {
      const fullNameError = getValidationError(
        "fullName",
        formData.fullName.value,
      );
      if (fullNameError) {
        updatedFormData.fullName.error = fullNameError;
        hasErrors = true;
      }
    }

    const emailError = getValidationError("email", formData.email.value);
    if (emailError) {
      updatedFormData.email.error = emailError;
      hasErrors = true;
    }

    if (isPublisher) {
      const addressError = getValidationError(
        "address",
        formData.address.value,
      );
      if (addressError) {
        updatedFormData.address.error = addressError;
        hasErrors = true;
      }
    }

    if (hasErrors) {
      setFormData(updatedFormData);
      return;
    }

    if (formData.role.value === "Publisher") {
      const userData = {
        companyName: formData.companyName.value,
        address: formData.address.value,
      };
      const res = await updateUser(0, userData);
      if (res.code === 200) {
        fetchUserData();
        notify("success", res?.message);
        setIsEditing(false);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } else {
      const userData = {
        name: formData.fullName.value,
      };
      const res = await updateUser(0, userData);
      if (res.code === 200) {
        fetchUserData();
        notify("success", res?.message);
        setIsEditing(false);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: value,
        error: "",
      },
    }));
  };

  // Password modal handlers
  const handleOpenPasswordModal = () => {
    setPasswordData(initialPasswordState);
    setIsPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setPasswordData(initialPasswordState);
  };

  const handlePasswordInputChange = (name, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: value,
        error: "",
      },
    }));
  };

  const togglePasswordVisibility = (field) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        showPassword: !prev[field].showPassword,
      },
    }));
  };

  const validatePasswordFields = () => {
    let isValid = true;
    const errors = { ...passwordData };

    // Validate old password
    if (!passwordData.oldPassword.value) {
      errors.oldPassword.error = "Old password is required";
      isValid = false;
    }

    // Validate new password
    if (!passwordData.newPassword.value) {
      errors.newPassword.error = "New password is required";
      isValid = false;
    } else if (passwordData.newPassword.value.length < 6) {
      errors.newPassword.error = "Password must be at least 6 characters";
      isValid = false;
    } else if (passwordData.newPassword.value === passwordData.oldPassword.value) {
      errors.newPassword.error = "New password must be different from old password";
      isValid = false;
    }

    // Validate confirm password
    if (!passwordData.confirmPassword.value) {
      errors.confirmPassword.error = "Please confirm your new password";
      isValid = false;
    } else if (passwordData.newPassword.value !== passwordData.confirmPassword.value) {
      errors.confirmPassword.error = "Passwords do not match";
      isValid = false;
    }

    setPasswordData(errors);
    return isValid;
  };

  const handlePasswordBlur = (name, value) => {
    let error = "";

    if (name === "oldPassword" && !value) {
      error = "Old password is required";
    } else if (name === "newPassword") {
      if (!value) {
        error = "New password is required";
      } else if (value.length < 6) {
        error = "Password must be at least 6 characters";
      } else if (value === passwordData.oldPassword.value) {
        error = "New password must be different from old password";
      }
    } else if (name === "confirmPassword") {
      if (!value) {
        error = "Please confirm your new password";
      } else if (value !== passwordData.newPassword.value) {
        error = "Passwords do not match";
      }
    }

    setPasswordData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        error: error,
      },
    }));
  };

  const handleChangePassword = async () => {
    if (!validatePasswordFields()) {
      return;
    }

    setIsSubmittingPassword(true);

    try {
      const payload = {
        oldPassword: passwordData.oldPassword.value,
        newPassword: passwordData.newPassword.value,
        confirmPassword: passwordData.confirmPassword.value,
      };

      const res = await userChangePassword(payload);

      if (res.code === 200) {
        notify("success", res?.message || "Password changed successfully");
        handleClosePasswordModal();
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to change password";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Something went wrong. Please try again.");
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "fullName":
        if (!value) return "Full name is required";
        else if (value.length < 3 || value.length > 100)
          return "Full name must be 3–100 characters";
        return "";

      case "companyName":
        if (!value) return "Company name is required";
        else if (value.length < 3 || value.length > 100)
          return "Company name must be 3–100 characters";
        return "";

      case "email":
        if (!value) return "Email is required";
        else if (!REGEX.EMAIL_REGEX.test(value)) return "Invalid email format";
        return "";

      case "mobileNo":
        if (!value) return "Mobile number is required";
        else if (!REGEX.PHONE.test(value)) return "Enter valid mobile number";
        return "";

      case "dateOfBirth":
        if (value && new Date(value) > new Date())
          return "Date of birth cannot be future date";
        return "";

      case "address":
        if (value.length > 250)
          return "Address should be less than 250 characters";
        return "";

      default:
        return "";
    }
  };

  const handleBlur = (name, value) => {
    const error = getValidationError(name, value);
    setFormData((prev) => ({
      ...prev,
      [name]: { ...prev[name], value, error },
    }));
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container className="mt--6" fluid>
        <Row>
          <Col xl="12">
            {/* Profile Summary */}
            <Card className="shadow mb-4">
              <CardBody className="position-relative">
                <Row className="align-items-center">
                  <Col
                    xs="12"
                    md="7"
                    className="text-center text-md-left mb-3 mb-md-0"
                  >
                    <h1 className="mb-0 text-vivid-blue">
                      {formData.role.value === "Publisher"
                        ? formData.companyName.value
                        : formData.fullName.value}
                    </h1>
                    <h3 className="text-muted d-block mb-2">
                      {formData.role.value}
                    </h3>
                    <Badge color="success" pill>
                      {formData.accountStatus.value}
                    </Badge>
                  </Col>

                  {/* Action Buttons */}
                  <div className="profile-action-btn position-absolute right-0 p-3 d-flex gap-2">
                    {!isEditing ? (
                      <>
                        <Button
                          className="btn-gradient-primary"
                          size="sm"
                          onClick={handleEditClick}
                        >
                          Edit Profile
                        </Button>
                        <Button
                          className="btn-outline-primary"
                          size="sm"
                          onClick={handleOpenPasswordModal}
                        >
                          Change Password
                        </Button>
                      </>
                    ) : (
                      <div className="d-flex">
                        <Button
                          color="secondary"
                          size="sm"
                          className="mr-2"
                          onClick={() => {
                            setIsEditing(false);
                          }}
                        >
                          Cancel
                        </Button>

                        <Button
                          className="btn-gradient-primary"
                          size="sm"
                          onClick={handleSave}
                        >
                          Save
                        </Button>
                      </div>
                    )}
                  </div>
                </Row>
              </CardBody>
            </Card>

            {/* Personal Information */}
            <Card className="shadow mb-4">
              <CardHeader className="border-bottom-1 d-flex flex-column m-0">
                <h3>Personal Information</h3>
              </CardHeader>

              <CardBody>
                <Row className="mb-3">
                  <Col md="4">
                    <Label className="form-control-label">
                      {formData.role.value === "Publisher"
                        ? "Company Name"
                        : "Full Name"}
                      <span className="text-danger ml-1">*</span>
                    </Label>

                    {isEditing ? (
                      <>
                        <Input
                          type="text"
                          name={
                            formData.role.value === "Publisher"
                              ? "companyName"
                              : "fullName"
                          }
                          maxLength={100}
                          value={
                            formData.role.value === "Publisher"
                              ? formData.companyName.value
                              : formData.fullName.value
                          }
                          className={
                            formData.role.value === "Publisher"
                              ? formData.companyName.error
                                ? "is-invalid"
                                : ""
                              : formData.fullName.error
                                ? "is-invalid"
                                : ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              formData.role.value === "Publisher"
                                ? "companyName"
                                : "fullName",
                              e.target.value,
                            )
                          }
                          onBlur={(e) =>
                            handleBlur(
                              formData.role.value === "Publisher"
                                ? "companyName"
                                : "fullName",
                              e.target.value,
                            )
                          }
                        />
                        {formData.role.value === "Publisher"
                          ? formData.companyName.error && (
                            <small className="invalid-feedback d-block">
                              {formData.companyName.error}
                            </small>
                          )
                          : formData.fullName.error && (
                            <small className="invalid-feedback d-block">
                              {formData.fullName.error}
                            </small>
                          )}
                      </>
                    ) : (
                      <p>
                        {formData.role.value === "Publisher"
                          ? formData.companyName.value
                          : formData.fullName.value}
                      </p>
                    )}
                  </Col>

                  <Col md="4">
                    <label className="form-control-label">Email</label>
                    <p>{formData.email.value || "-"}</p>
                  </Col>

                  {formData.role.value === "Publisher" && (
                    <Col md="8">
                      <label className="form-control-label">
                        Address
                        <span className="text-danger ml-1">*</span>
                      </label>
                      {isEditing ? (
                        <>
                          <Input
                            type="textarea"
                            name="address"
                            rows="3"
                            maxLength={250}
                            value={formData.address.value}
                            className={
                              formData.address.error ? "is-invalid" : ""
                            }
                            onChange={(e) =>
                              handleInputChange("address", e.target.value)
                            }
                            onBlur={(e) =>
                              handleBlur("address", e.target.value)
                            }
                          />
                          {formData.address.error && (
                            <small className="invalid-feedback d-block">
                              {formData.address.error}
                            </small>
                          )}
                        </>
                      ) : (
                        <p>{formData.address.value || "-"}</p>
                      )}
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>

            <Card className="shadow">
              <CardHeader className="border-bottom-1 d-flex flex-column">
                <h3 className="mb-2">Account Information</h3>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="4">
                    <label className="form-control-label">Role</label>
                    <p>{formData.role.value}</p>
                  </Col>
                  <Col md="4">
                    <label className="form-control-label">
                      Account Created On
                    </label>
                    <p>{formData.createdOn.value}</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Change Password Modal */}
      <Modal isOpen={isPasswordModalOpen} toggle={handleClosePasswordModal} size="md">
        <ModalHeader toggle={handleClosePasswordModal}>
          Change Password
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="oldPassword">
              Old Password <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type={passwordData.oldPassword.showPassword ? "text" : "password"}
                id="oldPassword"
                name="oldPassword"
                value={passwordData.oldPassword.value}
                className={passwordData.oldPassword.error ? "is-invalid" : ""}
                onChange={(e) =>
                  handlePasswordInputChange("oldPassword", e.target.value)
                }
                onBlur={(e) =>
                  handlePasswordBlur("oldPassword", e.target.value)
                }
                placeholder="Enter your old password"
              />
              <InputGroupText
                style={{ cursor: "pointer" }}
                onClick={() => togglePasswordVisibility("oldPassword")}
              >
                <i className={`fa ${passwordData.oldPassword.showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </InputGroupText>
            </InputGroup>
            {passwordData.oldPassword.error && (
              <small className="invalid-feedback d-block">
                {passwordData.oldPassword.error}
              </small>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="newPassword">
              New Password <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type={passwordData.newPassword.showPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword.value}
                className={passwordData.newPassword.error ? "is-invalid" : ""}
                onChange={(e) =>
                  handlePasswordInputChange("newPassword", e.target.value)
                }
                onBlur={(e) =>
                  handlePasswordBlur("newPassword", e.target.value)
                }
                placeholder="Enter new password (min. 6 characters)"
              />
              <InputGroupText
                style={{ cursor: "pointer" }}
                onClick={() => togglePasswordVisibility("newPassword")}
              >
                <i className={`fa ${passwordData.newPassword.showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </InputGroupText>
            </InputGroup>
            {passwordData.newPassword.error && (
              <small className="invalid-feedback d-block">
                {passwordData.newPassword.error}
              </small>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="confirmPassword">
              Confirm New Password <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <Input
                type={passwordData.confirmPassword.showPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword.value}
                className={passwordData.confirmPassword.error ? "is-invalid" : ""}
                onChange={(e) =>
                  handlePasswordInputChange("confirmPassword", e.target.value)
                }
                onBlur={(e) =>
                  handlePasswordBlur("confirmPassword", e.target.value)
                }
                placeholder="Confirm your new password"
              />
              <InputGroupText
                style={{ cursor: "pointer" }}
                onClick={() => togglePasswordVisibility("confirmPassword")}
              >
                <i className={`fa ${passwordData.confirmPassword.showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </InputGroupText>
            </InputGroup>
            {passwordData.confirmPassword.error && (
              <small className="invalid-feedback d-block">
                {passwordData.confirmPassword.error}
              </small>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClosePasswordModal}>
            Cancel
          </Button>
          <Button
            className="btn-gradient-primary"
            onClick={handleChangePassword}
            disabled={isSubmittingPassword}
          >
            {isSubmittingPassword ? "Changing..." : "Change Password"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default Profile;